import { useState, type CSSProperties, type ReactNode } from 'react';
import { C, F } from '../tokens';

type ButtonColor = 'acc' | 'gr' | 'bl' | 'am' | 're' | 'ghost' | 'pu';
type ButtonSize = 'sm' | 'md' | 'lg';

const bgMap: Record<ButtonColor, string> = {
  acc: 'linear-gradient(135deg,#E04A10,#FF5F1F)',
  gr: 'linear-gradient(135deg,#08A860,#0FD47F)',
  bl: 'linear-gradient(135deg,#1A6FD8,#3D8EFF)',
  am: 'linear-gradient(135deg,#C07800,#F5A623)',
  re: 'linear-gradient(135deg,#CC1C1C,#FF3B3B)',
  ghost: 'transparent',
  pu: 'linear-gradient(135deg,#7340E0,#9B6FFF)',
};

const shadowMap: Record<ButtonColor, string> = {
  acc: '0 4px 16px rgba(255,95,31,.3)',
  gr: '0 4px 16px rgba(15,212,127,.25)',
  bl: '0 4px 16px rgba(61,142,255,.25)',
  am: '0 4px 14px rgba(245,166,35,.22)',
  re: '0 4px 14px rgba(255,59,59,.25)',
  ghost: 'none',
  pu: '0 4px 14px rgba(155,111,255,.25)',
};

export interface ButtonProps {
  ch: ReactNode;
  onClick?: () => void;
  col?: ButtonColor;
  disabled?: boolean;
  loading?: boolean;
  fw?: boolean;
  sz?: ButtonSize;
  style?: CSSProperties;
  type?: 'button' | 'submit';
}

export function Button({ ch, onClick, col = 'acc', disabled, loading, fw, sz = 'md', style, type = 'button' }: ButtonProps) {
  const [down, setDown] = useState(false);
  const pad = sz === 'sm' ? '9px 16px' : sz === 'lg' ? '17px 22px' : '13px 18px';
  const fs = sz === 'sm' ? 11 : sz === 'lg' ? 16 : 13;

  return (
    <button
      type={type}
      onClick={!disabled && !loading ? onClick : undefined}
      onPointerDown={() => setDown(true)}
      onPointerUp={() => setDown(false)}
      onPointerLeave={() => setDown(false)}
      style={{
        width: fw ? '100%' : undefined,
        padding: pad,
        borderRadius: 12,
        border: col === 'ghost' ? `1px solid ${C.b}` : 'none',
        background: disabled ? C.card : bgMap[col],
        color: disabled ? C.dim : col === 'ghost' ? C.sub : '#fff',
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        fontFamily: F.head,
        fontWeight: 700,
        fontSize: fs,
        letterSpacing: '.7px',
        transform: down && !disabled ? 'scale(.97)' : 'scale(1)',
        transition: 'transform .1s ease,box-shadow .15s ease',
        boxShadow: disabled ? 'none' : shadowMap[col],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 7,
        ...style,
      }}
    >
      {loading ? <span style={{ animation: 'sp .8s linear infinite', display: 'inline-block', fontSize: 13 }}>↻</span> : null}
      {ch}
    </button>
  );
}
